import React from 'react';
import { Box, Card, CardContent, Typography, Chip, Tooltip, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { customerApi } from '../../api/client';
import { CustomerGeography, CustomerGeographyRow } from '../../types';
import { countryLabel, countryName } from '../../utils/countries';

const topCities = (rows: CustomerGeographyRow[], n = 8) => {
  const byCity = new Map<string, { label: string; count: number }>();
  for (const r of rows) {
    if (!r.city) continue;
    const key = `${r.city}|${r.country || ''}`;
    const entry = byCity.get(key) ?? { label: r.country ? `${r.city} (${r.country})` : r.city, count: 0 };
    entry.count += r.count;
    byCity.set(key, entry);
  }
  return Array.from(byCity.values()).sort((a, b) => b.count - a.count).slice(0, n);
};

const CityList: React.FC<{ title: string; rows: CustomerGeographyRow[] }> = ({ title, rows }) => {
  const cities = topCities(rows);
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase' }}>
        {title}
      </Typography>
      {cities.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Ingen</Typography>
      ) : (
        cities.map((c) => (
          <Box key={c.label} display="flex" justifyContent="space-between" sx={{ fontSize: '0.7rem', lineHeight: 1.6 }}>
            <span>{c.label}</span>
            <span style={{ color: 'rgba(0,0,0,0.5)' }}>{c.count}</span>
          </Box>
        ))
      )}
    </Box>
  );
};

/** Compact overview of where a customer ships from and to, based on its transports. */
export const CustomerGeographyCard: React.FC<{ customerId: string }> = ({ customerId }) => {
  const { data, isLoading } = useQuery<CustomerGeography>({
    queryKey: ['customer-geography', customerId],
    queryFn: async () => (await customerApi.getGeography(customerId)).data.data,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <Card sx={{ borderRadius: 1, mb: 1.5 }}>
      <CardContent sx={{ p: '10px 12px !important' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
          Geografi
        </Typography>

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={1}><CircularProgress size={16} /></Box>
        ) : !data || data.countries.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
            Ingen forsendelsesdata
          </Typography>
        ) : (
          <>
            <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.75}>
              {data.countries.slice(0, 12).map((c) => (
                <Tooltip key={c.code} title={`${countryLabel(c.code)}: ${c.asOrigin} som afsender, ${c.asDestination} som modtager`}>
                  <Chip
                    size="small"
                    label={`${countryName(c.code)} · fra ${c.asOrigin} / til ${c.asDestination}`}
                    sx={{ height: 20, fontSize: '0.65rem' }}
                    variant="outlined"
                  />
                </Tooltip>
              ))}
            </Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5} mt={1}>
              <CityList title="Afsender" rows={data.origins} />
              <CityList title="Modtager" rows={data.destinations} />
            </Box>
            {data.includesChildren && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block', mt: 0.75 }}>
                Inkl. datterselskaber
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerGeographyCard;
